import { config } from "../Environment";
import type { FeatureArea } from "../lib/feedbackFeatureAreas";

export type FeedbackType = "bug" | "suggestion";
export type FeedbackStatus = "new" | "seen" | "resolved";

export interface FeedbackScreenshot {
  url: string;
  storageKey: string;
  mimeType: string;
  size: number;
  width?: number;
  height?: number;
}

export interface FeedbackContext {
  route: string;
  label: string;
  userAgent: string;
  previousRoute?: string;
  previousLabel?: string;
  screenshot?: FeedbackScreenshot;
  featureArea?: FeatureArea;
}

export interface FeedbackSubmitter {
  _id: string;
  email: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
}

export interface FeedbackSubmission {
  _id: string;
  type: FeedbackType;
  message: string;
  fiSubmitter: FeedbackSubmitter | string;
  submitterRoles: string[];
  context: FeedbackContext;
  status: FeedbackStatus;
  fiAssignee?: FeedbackSubmitter | string;
  wasReassigned: boolean;
  createdOn: number;
  lastUpdated: number;
}

export interface FeedbackAssignment {
  _id: string;
  featureArea: FeatureArea;
  fiAssignee: FeedbackSubmitter;
  assignedBy: FeedbackSubmitter;
  lastUpdated: number;
}

export interface FeedbackFallbackAssignee {
  _id: string;
  fiAssignee: FeedbackSubmitter;
  lastUpdated: number;
}

/** Submit an in-app feedback report (bug or suggestion) */
export const submitFeedback = async (
  retryFetch: Function,
  data: { type: FeedbackType; message: string; context: FeedbackContext }
): Promise<{ type: string; message: string }> => {
  const res = await retryFetch(`${config.API_BASE}/api/feedback`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  return res.json();
};

/** Admin: list in-app feedback submissions */
export const listFeedbackSubmissions = async (
  retryFetch: Function,
  params?: { status?: FeedbackStatus; limit?: number; skip?: number }
): Promise<{ data: FeedbackSubmission[]; total: number; hasMore: boolean }> => {
  const query = new URLSearchParams();
  if (params?.status) query.set("status", params.status);
  if (params?.limit) query.set("limit", String(params.limit));
  if (params?.skip) query.set("skip", String(params.skip));
  const qs = query.toString();

  const res = await retryFetch(
    `${config.API_BASE}/api/sysadmin/feedback${qs ? `?${qs}` : ""}`,
    { credentials: "include" }
  );
  if (!res.ok) throw new Error(`Failed to list feedback: ${res.statusText}`);
  return res.json();
};

/** Admin: update a feedback submission's status */
export const updateFeedbackStatus = async (
  retryFetch: Function,
  id: string,
  status: FeedbackStatus
): Promise<{ data: FeedbackSubmission }> => {
  const res = await retryFetch(`${config.API_BASE}/api/sysadmin/feedback/${id}/status`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ status }),
  });
  if (!res.ok) throw new Error(`Failed to update feedback status: ${res.statusText}`);
  return res.json();
};

/** Get a presigned URL for uploading a feedback screenshot to R2 */
export const getFeedbackScreenshotUploadUrl = async (
  retryFetch: Function,
  mimeType: string
): Promise<{ uploadUrl: string; key: string; expiresIn: number; maxSize: number }> => {
  const res = await retryFetch(`${config.API_BASE}/api/feedback/images/presigned-url`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ mimeType }),
  });
  if (!res.ok) {
    const error = await res.json().catch(() => ({ error: "Failed to get presigned URL" }));
    throw new Error(error.error || "Failed to get presigned URL");
  }
  return res.json();
};

/** Confirm a feedback screenshot upload and get back its metadata */
export const confirmFeedbackScreenshotUpload = async (
  retryFetch: Function,
  data: { key: string; size: number; mimeType: string; width?: number; height?: number }
): Promise<{ message: string; image: FeedbackScreenshot }> => {
  const res = await retryFetch(`${config.API_BASE}/api/feedback/images/confirm`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const error = await res.json().catch(() => ({ error: "Failed to save screenshot" }));
    throw new Error(error.error || "Failed to save screenshot");
  }
  return res.json();
};

/** Admin: list every fixed feature area, its mapping (if any), and the fallback assignee */
export const listFeedbackAssignments = async (
  retryFetch: Function
): Promise<{ featureAreas: FeatureArea[]; assignments: FeedbackAssignment[]; fallback: FeedbackFallbackAssignee | null }> => {
  const res = await retryFetch(`${config.API_BASE}/api/sysadmin/feedback/assignments`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error(`Failed to list feedback assignments: ${res.statusText}`);
  return res.json();
};

/** Admin: set (or replace) the assignee for one feature area */
export const setFeedbackAssignment = async (
  retryFetch: Function,
  featureArea: FeatureArea,
  userId: string
): Promise<{ data: FeedbackAssignment }> => {
  const res = await retryFetch(`${config.API_BASE}/api/sysadmin/feedback/assignments/${featureArea}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ userId }),
  });
  if (!res.ok) throw new Error(`Failed to set feedback assignment: ${res.statusText}`);
  return res.json();
};

/** Admin: set (or replace) the single fallback assignee */
export const setFeedbackFallbackAssignee = async (
  retryFetch: Function,
  userId: string
): Promise<{ data: FeedbackFallbackAssignee }> => {
  const res = await retryFetch(`${config.API_BASE}/api/sysadmin/feedback/fallback-assignee`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ userId }),
  });
  if (!res.ok) throw new Error(`Failed to set fallback assignee: ${res.statusText}`);
  return res.json();
};

/** Admin: reassign one specific feedback submission */
export const reassignFeedback = async (
  retryFetch: Function,
  id: string,
  userId: string
): Promise<{ data: FeedbackSubmission }> => {
  const res = await retryFetch(`${config.API_BASE}/api/sysadmin/feedback/${id}/assignee`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ userId }),
  });
  if (!res.ok) throw new Error(`Failed to reassign feedback: ${res.statusText}`);
  return res.json();
};
